import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Icon,
  MenuItem,
  Typography,
} from '@material-ui/core'
import { Clock } from 'react-feather'
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog } from '../../../dialog/Dialog'
import { ControlledTextField } from '../../../form/ControlledTextField'
import { FormGroup } from '../../../form/FormGroup'
import { CalibrationFormValues } from './calibration-form-values'
import { theme } from '../../../../theme'
import { validators } from '../../../../validation/validators'
import { areaUnit, temperatureUnit } from '../../../../util/formatters'
import { ControlledDatePicker } from '../../../form/ControlledDatePicker'
import { ControlledToggleButton } from '../../../form/ControlledToggleButton'
import {
  HourSeriesFragment,
  HourSeriesType,
} from '../../../../generated/graphql'

type Props = {
  open: boolean
  onSubmit: (values: CalibrationFormValues) => Promise<void>
  defaultValues?: Partial<CalibrationFormValues>
  onClose: () => void
  measuredHourSeries: HourSeriesFragment[]
}

export function CalibrationDataDialog({
  open,
  onSubmit,
  defaultValues,
  onClose,
  measuredHourSeries,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { control, handleSubmit, errors, reset } =
    useForm<CalibrationFormValues>({
      defaultValues,
    })

  useEffect(() => {
    if (open) {
      reset(defaultValues)
    }
  }, [open, defaultValues, reset])

  return (
    <Dialog
      title={t('buildingEnergyModels.calibrationDialog.title')}
      maxWidth="md"
      open={open}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <FormGroup>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="description"
                  error={errors.description}
                  label={t(
                    'buildingEnergyModels.calibrationDialog.description',
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  control={control}
                  name="area"
                  error={errors.area}
                  label={t('buildingEnergyModels.calibrationDialog.area')}
                  rules={{
                    ...validators.required,
                    ...validators.positiveDecimal,
                  }}
                  InputProps={{
                    endAdornment: <Typography>{areaUnit}</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  control={control}
                  name="heatingCorrection"
                  error={errors.heatingCorrection}
                  label={t(
                    'buildingEnergyModels.calibrationDialog.heatingCorrection',
                  )}
                  rules={validators.required}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledDatePicker
                  control={control}
                  name="measuredStart"
                  label={t(
                    'buildingEnergyModels.calibrationDialog.measuredStart',
                  )}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Typography>
                  {t('buildingEnergyModels.calibrationDialog.hourSeriesLabel')}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  control={control}
                  name="outdoorTempHsId"
                  error={errors.outdoorTempHsId}
                  label={t(
                    'buildingEnergyModels.calibrationDialog.outdoorTempHs',
                  )}
                  rules={validators.required}
                  select
                >
                  {Object.values(
                    measuredHourSeries
                      .filter(
                        (hs) =>
                          hs.type === HourSeriesType.MeasuredOutdoorTemperature,
                      )
                      .map((hs) => (
                        <MenuItem key={hs.id} value={hs.id}>
                          {hs.name}
                        </MenuItem>
                      )),
                  )}
                </ControlledTextField>
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  control={control}
                  name="heatingHsId"
                  error={errors.heatingHsId}
                  label={t('buildingEnergyModels.calibrationDialog.heatingHs')}
                  rules={validators.required}
                  select
                >
                  {Object.values(
                    measuredHourSeries
                      .filter(
                        (hs) =>
                          hs.type === HourSeriesType.MeasuredHeatingDemand,
                      )
                      .map((hs) => (
                        <MenuItem key={hs.id} value={hs.id}>
                          {hs.name}
                        </MenuItem>
                      )),
                  )}
                </ControlledTextField>
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  control={control}
                  name="electricityHsId"
                  error={errors.electricityHsId}
                  label={t(
                    'buildingEnergyModels.calibrationDialog.electricityHs',
                  )}
                  rules={validators.required}
                  select
                >
                  {Object.values(
                    measuredHourSeries
                      .filter(
                        (hs) =>
                          hs.type === HourSeriesType.MeasuredElectricityDemand,
                      )
                      .map((hs) => (
                        <MenuItem key={hs.id} value={hs.id}>
                          {hs.name}
                        </MenuItem>
                      )),
                  )}
                </ControlledTextField>
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  control={control}
                  name="coolingHsId"
                  error={errors.coolingHsId}
                  label={t('buildingEnergyModels.calibrationDialog.coolingHs')}
                  rules={validators.required}
                  select
                >
                  {Object.values(
                    measuredHourSeries
                      .filter(
                        (hs) =>
                          hs.type === HourSeriesType.MeasuredCoolingDemand,
                      )
                      .map((hs) => (
                        <MenuItem key={hs.id} value={hs.id}>
                          {hs.name}
                        </MenuItem>
                      )),
                  )}
                </ControlledTextField>
              </Grid>
            </Grid>

            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs={12}>
                <Typography>
                  {t('buildingEnergyModels.calibrationDialog.daysLabel')}
                </Typography>
              </Grid>
              {Array.from(Array(7)).map((_, index) => (
                <Grid item key={`dayRow-${index.toString()}`}>
                  <ControlledToggleButton
                    key={`day-${index.toString()}`}
                    control={control}
                    name={`weeklyProfile.${index}`}
                    label={t(`date.weekdays.${index + 1}`)}
                  />
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs={12}>
                <Typography>
                  {t('buildingEnergyModels.calibrationDialog.monthsLabel')}
                </Typography>
              </Grid>
              {Array.from(Array(12)).map((_, index) => (
                <Grid item key={`monthRow-${index.toString()}`}>
                  <ControlledToggleButton
                    key={`month-${index.toString()}`}
                    control={control}
                    name={`monthlyProfile.${index}`}
                    label={t(`date.months.${index + 1}`)}
                  />
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs={4}>
                <Typography>
                  {t('buildingEnergyModels.calibrationDialog.winterTempRange')}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography>
                  {t('buildingEnergyModels.calibrationDialog.zeroTempRange')}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography>
                  {t('buildingEnergyModels.calibrationDialog.summerTempRange')}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <ControlledTextField
                  control={control}
                  name="winterTempStart"
                  error={errors.winterTempStart}
                  label={t(
                    'buildingEnergyModels.calibrationDialog.tempRangeStart',
                  )}
                  rules={validators.required}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <ControlledTextField
                  control={control}
                  name="winterTempEnd"
                  error={errors.winterTempEnd}
                  label={t(
                    'buildingEnergyModels.calibrationDialog.tempRangeEnd',
                  )}
                  rules={validators.required}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <ControlledTextField
                  control={control}
                  name="zeroWeatherTempStart"
                  error={errors.zeroWeatherTempStart}
                  label={t(
                    'buildingEnergyModels.calibrationDialog.tempRangeStart',
                  )}
                  rules={validators.required}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <ControlledTextField
                  control={control}
                  name="zeroWeatherTempEnd"
                  error={errors.zeroWeatherTempEnd}
                  label={t(
                    'buildingEnergyModels.calibrationDialog.tempRangeEnd',
                  )}
                  rules={validators.required}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <ControlledTextField
                  control={control}
                  name="summerTempStart"
                  error={errors.summerTempStart}
                  label={t(
                    'buildingEnergyModels.calibrationDialog.tempRangeStart',
                  )}
                  rules={validators.required}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <ControlledTextField
                  control={control}
                  name="summerTempEnd"
                  error={errors.summerTempEnd}
                  label={t(
                    'buildingEnergyModels.calibrationDialog.tempRangeEnd',
                  )}
                  rules={validators.required}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography>
                      {t(
                        'buildingEnergyModels.calibrationDialog.nightTimeRange',
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      control={control}
                      name="nightStartHour"
                      error={errors.nightStartHour}
                      label={t(
                        'buildingEnergyModels.calibrationDialog.timeRangeStart',
                      )}
                      rules={validators.required}
                      InputProps={{
                        endAdornment: (
                          <Icon>
                            <Clock />
                          </Icon>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      control={control}
                      name="nightEndHour"
                      error={errors.nightEndHour}
                      label={t(
                        'buildingEnergyModels.calibrationDialog.timeRangeEnd',
                      )}
                      rules={validators.required}
                      InputProps={{
                        endAdornment: (
                          <Icon>
                            <Clock />
                          </Icon>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography>
                      {t(
                        'buildingEnergyModels.calibrationDialog.morningTimeRange',
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      control={control}
                      name="morningStartHour"
                      error={errors.morningStartHour}
                      label={t(
                        'buildingEnergyModels.calibrationDialog.timeRangeStart',
                      )}
                      rules={validators.required}
                      InputProps={{
                        endAdornment: (
                          <Icon>
                            <Clock />
                          </Icon>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      control={control}
                      name="morningEndHour"
                      error={errors.morningEndHour}
                      label={t(
                        'buildingEnergyModels.calibrationDialog.timeRangeEnd',
                      )}
                      rules={validators.required}
                      InputProps={{
                        endAdornment: (
                          <Icon>
                            <Clock />
                          </Icon>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography>
                      {t('buildingEnergyModels.calibrationDialog.dayTimeRange')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      control={control}
                      name="dayStartHour"
                      error={errors.dayStartHour}
                      label={t(
                        'buildingEnergyModels.calibrationDialog.timeRangeStart',
                      )}
                      rules={validators.required}
                      InputProps={{
                        endAdornment: (
                          <Icon>
                            <Clock />
                          </Icon>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      control={control}
                      name="dayEndHour"
                      error={errors.dayEndHour}
                      label={t(
                        'buildingEnergyModels.calibrationDialog.timeRangeEnd',
                      )}
                      rules={validators.required}
                      InputProps={{
                        endAdornment: (
                          <Icon>
                            <Clock />
                          </Icon>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography>
                      {t(
                        'buildingEnergyModels.calibrationDialog.eveningTimeRange',
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      control={control}
                      name="eveningStartHour"
                      error={errors.eveningStartHour}
                      label={t(
                        'buildingEnergyModels.calibrationDialog.timeRangeStart',
                      )}
                      rules={validators.required}
                      InputProps={{
                        endAdornment: (
                          <Icon>
                            <Clock />
                          </Icon>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      control={control}
                      name="eveningEndHour"
                      error={errors.eveningEndHour}
                      label={t(
                        'buildingEnergyModels.calibrationDialog.timeRangeEnd',
                      )}
                      rules={validators.required}
                      InputProps={{
                        endAdornment: (
                          <Icon>
                            <Clock />
                          </Icon>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Box display="flex" flexGrow={1} gridGap={theme.spacing(1)}>
            <Box flexGrow={1} />
            <Button variant="contained" onClick={onClose}>
              {t('common.cancel')}
            </Button>

            <Button type="submit" variant="contained" color="primary">
              {t('common.saveChanges')}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  )
}
